import moment from 'moment';

import {
  CreatorTierType,
  IUser,
  PlanTierType,
} from '../types/userTypes';

export const PLAN_LABEL_BY_TIER: Record<string, string> = {
  tier1: 'Basic',
  tier2: 'Pro',
  tier3: 'Advanced',
  tier4: 'Ultimate',
};

export const getPlanLabel = (tier: CreatorTierType | PlanTierType | undefined) =>
  (tier && PLAN_LABEL_BY_TIER[tier]) || 'current';

export const getAccountLossDate = (expiry?: string) =>
  expiry ? moment(expiry).add(15, 'days').format('DD MMM YYYY') : '';

export const getContentLossDate = (expiry?: string) =>
  expiry ? moment(expiry).add(30, 'days').format('DD MMM YYYY') : '';

// Thresholds shared with the web platform / 2.0 (calendar days since expiry).
const LOCK_AFTER_DAYS = 15; // diffDays > 15 ⇒ locked
const PRE_EXPIRY_WINDOW_DAYS = 7; // diffDays >= -7 ⇒ pre-expiry
const ACTIVE_AUTOPAY_STATUSES = ['active', 'authenticated'];

const toValidMoment = (value?: string | null) => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date : null;
};

/** `today − expiry` in calendar days (negative before expiry). */
const daysSinceExpiry = (expiry: moment.Moment) =>
  moment().startOf('day').diff(expiry.clone().startOf('day'), 'days');

// Creator on a paid tier on a whitelabel host. Tier is sourced from
// hostMetadata.creator (stable, same for creator + UAM sub-users), not from the
// user record — user.userTier gets wiped by intermediate user-detail refetches.
const isEligibleCreator = (
  user: IUser,
  creatorTier: PlanTierType | CreatorTierType | undefined,
  isTagMango: boolean,
) => {
  if (isTagMango) return false;
  if (!creatorTier || creatorTier === 'tier1') return false;
  return user.type === 'creator_completed' || user.type === 'creator_restricted';
};

export interface ScheduledPlan {
  /** When it starts, for copy: "30 October at 12:00 AM". */
  activationLabel: string;
  /** Its plan name ("Advanced"), or null until the backend sends the tier. */
  planName: string | null;
  /** First day the account locks if the current plan is not renewed
   *  ("26 October"): the day after the grace period ends. */
  accessRestrictedFromLabel: string | null;
  /**
   * It starts before the grace period runs out (overlapping the current plan,
   * or at most 15 days after expiry), so the account never locks and no
   * expiry notice shows (TMG-3904).
   */
  coversExpiry: boolean;
}

/**
 * The tier plan already bought and queued to start later (TMG-3904), or null.
 * A date already in the past still counts: the backend keeps sending it until
 * its activation job switches the plan.
 */
export const getScheduledPlan = (user: IUser): ScheduledPlan | null => {
  const activateAt = toValidMoment(user.scheduledPlanActivateAt);
  if (!activateAt) return null;
  const expiry = toValidMoment(user.accountSettingTierSubscriptionExpiry);
  const tier = user.scheduledPlanTier;
  return {
    activationLabel: activateAt.format('D MMMM [at] h:mm A'),
    planName: (tier && PLAN_LABEL_BY_TIER[tier]) || null,
    accessRestrictedFromLabel: expiry
      ? expiry
          .clone()
          .startOf('day')
          .add(LOCK_AFTER_DAYS + 1, 'days')
          .format('D MMMM')
      : null,
    coversExpiry:
      !expiry ||
      activateAt
        .clone()
        .startOf('day')
        .diff(expiry.clone().startOf('day'), 'days') <= LOCK_AFTER_DAYS,
  };
};

/** "today" / "in 1 day" / "in N days" until the current plan expires. */
const formatDaysUntilExpiry = (expiry: moment.Moment) => {
  const days = Math.max(0, -daysSinceExpiry(expiry));
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
};

/**
 * What the tier-expiry modal should show (null ⇒ nothing):
 * - `locked`: >15 days past expiry, nothing scheduled — the red blocking card.
 * - `scheduledLocked`: locked, but a plan is bought and starts past the grace
 *   period — the blocking "Plan Activation Scheduled" card instead.
 * - `scheduledNotice`: a plan starting past the grace period, account not yet
 *   locked (≤7 days before expiry, or in grace). The PWA has no nav bar, so the
 *   2.0 strip copy is shown in a dismissible modal (TMG-3904 comments).
 * A plan starting within the grace period suppresses everything.
 */
export type TierExpiryView =
  | { kind: 'locked' }
  | { kind: 'scheduledLocked'; plan: ScheduledPlan }
  | { kind: 'scheduledNotice'; message: string }
  | null;

export const getTierExpiryView = (
  user: IUser,
  creatorTier: PlanTierType | CreatorTierType | undefined,
  isTagMango: boolean,
): TierExpiryView => {
  if (!isEligibleCreator(user, creatorTier, isTagMango)) return null;
  const expiry = toValidMoment(user.accountSettingTierSubscriptionExpiry);
  if (!expiry) return null;

  const plan = getScheduledPlan(user);
  if (plan?.coversExpiry) return null;

  const diffDays = daysSinceExpiry(expiry);
  if (diffDays > LOCK_AFTER_DAYS) {
    return plan ? { kind: 'scheduledLocked', plan } : { kind: 'locked' };
  }
  if (!plan) return null;

  const newPlan = plan.planName
    ? `your new plan, ${plan.planName},`
    : 'your new plan';
  const untilActive = `until ${newPlan} becomes active on ${plan.activationLabel}.`;

  if (diffDays > 0 && plan.accessRestrictedFromLabel) {
    return {
      kind: 'scheduledNotice',
      message: `Your plan has expired. You will lose access to your account from ${plan.accessRestrictedFromLabel} ${untilActive}`,
    };
  }
  const autopayActive = ACTIVE_AUTOPAY_STATUSES.includes(
    user.accountSettingTierRazorpaySubscriptionIsAutopayStatus ?? '',
  );
  if (diffDays <= 0 && diffDays >= -PRE_EXPIRY_WINDOW_DAYS && !autopayActive) {
    return {
      kind: 'scheduledNotice',
      message: `Your ${getPlanLabel(creatorTier)} plan will expire ${formatDaysUntilExpiry(expiry)}. Account access will be restricted ${LOCK_AFTER_DAYS} days after expiry ${untilActive}`,
    };
  }
  return null;
};

export const isUAMUser = (user: IUser) => user.type === 'creator_restricted';
